import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension, wrappingInputRule } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import StarterKit from "@tiptap/starter-kit";
import { OrderedList, orderedListInputRegex } from "@tiptap/extension-list";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Image as ImageIcon,
  GripHorizontal,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import { getRoleLabel } from "@/utils/roleLabel";
import { uploadErrorMessage } from "@/utils/managedFile";

// Ordered list without joinPredicate — allows arbitrary start numbers (e.g. 3. 4. 5.)
// and prevents auto-renumbering that would reset "3. 4." to "1. 2."
const FreeOrderedList = OrderedList.extend({
  addInputRules() {
    return [
      wrappingInputRule({
        find: orderedListInputRegex,
        type: this.type,
        getAttributes: (match) => ({ start: +match[1] }),
      }),
    ];
  },
});

export interface MentionSuggestion {
  id: string;
  name: string;
  role?: string;
  is_ceo?: boolean;
}

interface MentionDropdown {
  users: MentionSuggestion[];
  top: number;
  left: number;
  activeIdx: number;
}

interface Props {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  onImageUpload?: (file: File) => Promise<string>;
  mentionSuggestions?: MentionSuggestion[];
  onMentionIdsChange?: (ids: string[]) => void;
  /** Starting height of the editing area in px; the user can still drag-resize it. */
  initialHeight?: number;
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      disabled={disabled}
      title={title}
      className={[
        "p-1.5 rounded text-sm transition-colors",
        active
          ? "bg-[var(--color-primary)] text-white"
          : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
        disabled ? "opacity-40 cursor-not-allowed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  value,
  onChange,
  disabled = false,
  placeholder = "Napisz komentarz…",
  onImageUpload,
  mentionSuggestions,
  onMentionIdsChange,
  initialHeight = 120,
}: Props) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [, setTick] = useState(0);
  const [editorHeight, setEditorHeight] = useState(initialHeight);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [mentionDropdown, setMentionDropdown] = useState<MentionDropdown | null>(null);
  const resizeDrag = useRef<{ startY: number; startH: number } | null>(null);

  // Mention chips state
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addDropPos, setAddDropPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);

  // Refs so that editorProps callbacks always see the latest values without re-creating the editor
  const onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;
  const mentionSuggestionsRef = useRef(mentionSuggestions);
  mentionSuggestionsRef.current = mentionSuggestions;
  const onMentionIdsChangeRef = useRef(onMentionIdsChange);
  onMentionIdsChangeRef.current = onMentionIdsChange;
  const mentionDropdownRef = useRef(mentionDropdown);
  mentionDropdownRef.current = mentionDropdown;

  // Track mention names for in-editor highlighting (ref so plugin always reads latest)
  const mentionHighlightNamesRef = useRef<string[]>([]);
  mentionHighlightNamesRef.current = (mentionSuggestions ?? []).map((s) => s.name);

  const MentionHighlightExtension = useMemo(() =>
    Extension.create({
      name: "mentionHighlight",
      addProseMirrorPlugins() {
        return [
          new Plugin({
            key: new PluginKey("mentionHighlight"),
            props: {
              decorations(state) {
                const names = mentionHighlightNamesRef.current;
                if (!names.length) return DecorationSet.empty;
                const decos: Decoration[] = [];
                state.doc.descendants((node, pos) => {
                  if (!node.isText || !node.text) return;
                  const text = node.text;
                  names.forEach((name) => {
                    const re = new RegExp(
                      `@${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
                      "g"
                    );
                    let m: RegExpExecArray | null;
                    while ((m = re.exec(text)) !== null) {
                      decos.push(
                        Decoration.inline(pos + m.index, pos + m.index + m[0].length, {
                          class: "mention-in-editor",
                        })
                      );
                    }
                  });
                });
                return DecorationSet.create(state.doc, decos);
              },
            },
          }),
        ];
      },
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Must be defined before useEditor so it can be referenced in editorProps
  const handleImageFileRef = useRef<(file: File) => Promise<void>>(async () => {});

  const insertMention = useCallback((editor: ReturnType<typeof useEditor>, user: MentionSuggestion) => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const textBefore = editor.state.doc.textBetween(Math.max(0, from - 100), from, "\n");
    const match = textBefore.match(/(@[^\s@]*)$/);
    if (!match) return;
    const matchStart = from - match[0].length;
    editor.chain().focus().deleteRange({ from: matchStart, to: from }).insertContent(`@${user.name} `).run();
    setMentionDropdown(null);
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        orderedList: false,
      }),
      FreeOrderedList,
      Underline,
      Image.configure({ inline: false, allowBase64: true }),
      MentionHighlightExtension,
    ],
    content: value || "",
    editable: !disabled,
    onUpdate({ editor }) {
      // editor.isEmpty is true even for an empty list item (isNodeEmpty is recursive).
      // Only treat as empty when the document is literally just one empty paragraph.
      const doc = editor.state.doc;
      const isActuallyEmpty =
        editor.isEmpty &&
        doc.childCount === 1 &&
        doc.firstChild?.type.name === "paragraph";
      const html = isActuallyEmpty ? "" : editor.getHTML();
      onChange(html);

      // Sync mention IDs by scanning plain text for @Name matches
      const suggestions = mentionSuggestionsRef.current;
      const onIdsChange = onMentionIdsChangeRef.current;
      if (suggestions) {
        const text = editor.getText();
        const ids = suggestions.filter((s) => text.includes(`@${s.name}`)).map((s) => s.id);
        setMentionedIds(ids);
        onIdsChange?.(ids);
      }
    },
    onTransaction({ editor }) {
      setTick((t) => t + 1);

      // Detect @query before cursor and show/hide suggestion dropdown
      const suggestions = mentionSuggestionsRef.current;
      if (!suggestions?.length) return;

      const { from } = editor.state.selection;
      const textBefore = editor.state.doc.textBetween(Math.max(0, from - 100), from, "\n");
      const match = textBefore.match(/(@[^\s@]*)$/);

      if (match) {
        const query = match[1].slice(1).toLowerCase();
        const filtered = suggestions
          .filter((s) => s.name.toLowerCase().includes(query))
          .slice(0, 8);

        if (filtered.length) {
          const matchStart = from - match[0].length;
          const coords = editor.view.coordsAtPos(matchStart);
          setMentionDropdown((prev) => ({
            users: filtered,
            top: coords.bottom + 4,
            left: coords.left,
            activeIdx: prev?.activeIdx ?? 0,
          }));
          return;
        }
      }
      setMentionDropdown(null);
    },
    editorProps: {
      attributes: {
        class: "focus:outline-none text-sm text-gray-800 dark:text-gray-200 h-full",
      },
      handleDrop(_view, event) {
        if (event.dataTransfer?.files?.[0]?.type.startsWith("image/")) return true;
        return false;
      },
      handleKeyDown(_view, event) {
        const dropdown = mentionDropdownRef.current;
        if (!dropdown) return false;

        if (event.key === "ArrowDown") {
          event.preventDefault();
          setMentionDropdown((d) => d ? { ...d, activeIdx: Math.min(d.activeIdx + 1, d.users.length - 1) } : d);
          return true;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setMentionDropdown((d) => d ? { ...d, activeIdx: Math.max(d.activeIdx - 1, 0) } : d);
          return true;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          const user = dropdown.users[dropdown.activeIdx];
          if (user) {
            event.preventDefault();
            // Editor ref is available in closure — use the editor from outer scope via ref
            editorRef.current && insertMention(editorRef.current, user);
            return true;
          }
        }
        if (event.key === "Escape") {
          setMentionDropdown(null);
          return true;
        }
        return false;
      },
    },
  });

  // Keep ref to editor for use in handleKeyDown closure
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // Wire up the actual handler now that we have editor in scope
  handleImageFileRef.current = async (file: File) => {
    if (!editor || !onImageUploadRef.current) return;
    setUploadError(null);
    setUploadingImage(true);
    try {
      const url = await onImageUploadRef.current(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      // The API's own reason first — axios' "Request failed with status code 400" says nothing.
      const msg = uploadErrorMessage(err, err instanceof Error ? err.message : "Błąd uploadu");
      setUploadError(`Nie udało się wstawić obrazu: ${msg}`);
    } finally {
      setUploadingImage(false);
    }
  };

  // Sync external value changes (form reset populating saved data, clearing after send).
  // No-op while typing: onUpdate echoes the same HTML back through the parent.
  useEffect(() => {
    if (!editor) return;
    const doc = editor.state.doc;
    const isActuallyEmpty =
      editor.isEmpty && doc.childCount === 1 && doc.firstChild?.type.name === "paragraph";
    const currentHtml = isActuallyEmpty ? "" : editor.getHTML();
    if ((value || "") !== currentHtml) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, value]);

  function onResizeStart(e: React.MouseEvent) {
    e.preventDefault();
    resizeDrag.current = { startY: e.clientY, startH: editorHeight };
    const onMove = (ev: MouseEvent) => {
      if (!resizeDrag.current) return;
      setEditorHeight(Math.max(80, resizeDrag.current.startH + ev.clientY - resizeDrag.current.startY));
    };
    const onUp = () => {
      resizeDrag.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  const removeMention = useCallback((userId: string) => {
    if (!editor) return;
    const user = mentionSuggestionsRef.current?.find((s) => s.id === userId);
    if (!user) return;
    const html = editor.getHTML();
    const escaped = user.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Strip only the leading @, leaving the name in place
    const updated = html.replace(new RegExp(`@(${escaped})`, "g"), "$1");
    editor.commands.setContent(updated);
  }, [editor]);

  const addMentionFromPicker = useCallback((user: MentionSuggestion) => {
    if (!editor) return;
    editor.chain().focus().insertContent(`@${user.name} `).run();
    setAddOpen(false);
    setAddSearch("");
  }, [editor]);

  const openAddPicker = useCallback(() => {
    if (disabled) return;
    const btn = addBtnRef.current;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      // Dropdown approx height: search header (~44px) + max-h-52 list (208px).
      const DROPDOWN_HEIGHT = 252;
      const spaceBelow = window.innerHeight - rect.bottom;
      // Flip the dropdown above the button when it would overflow the bottom
      // of the viewport (e.g. the comment composer near the page bottom).
      if (spaceBelow < DROPDOWN_HEIGHT && rect.top > spaceBelow) {
        setAddDropPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
      } else {
        setAddDropPos({ top: rect.bottom + 4, left: rect.left });
      }
    }
    setAddOpen((v) => !v);
    setAddSearch("");
  }, [disabled]);

  useEffect(() => {
    if (!addOpen) return;
    const handler = (e: MouseEvent) => {
      if (addBtnRef.current && addBtnRef.current.contains(e.target as Node)) return;
      setAddOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [addOpen]);

  if (!editor) return null;

  return (
    <>
      <div
        className={[
          "border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 flex flex-col",
          disabled ? "opacity-60" : "",
          isDragOver ? "border-[var(--color-primary)]" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          setIsDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file?.type.startsWith("image/") && onImageUpload) {
            e.preventDefault();
            handleImageFileRef.current(file);
          }
        }}
      >
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 px-2 py-1 border-b border-gray-100 dark:border-gray-700 flex-wrap">
          <select
            onChange={(e) => {
              const val = e.target.value;
              if (val === "p") {
                editor.chain().focus().setParagraph().run();
              } else {
                editor.chain().focus().toggleHeading({ level: Number(val) as 1 | 2 | 3 }).run();
              }
            }}
            value={
              editor.isActive("heading", { level: 1 }) ? "1"
              : editor.isActive("heading", { level: 2 }) ? "2"
              : editor.isActive("heading", { level: 3 }) ? "3"
              : "p"
            }
            disabled={disabled}
            className="text-xs px-1.5 py-1 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-40 cursor-pointer focus:outline-none"
          >
            <option value="p">Normalny</option>
            <option value="1">Nagłówek 1</option>
            <option value="2">Nagłówek 2</option>
            <option value="3">Nagłówek 3</option>
          </select>

          <div className="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            disabled={disabled}
            title="Pogrubienie (Ctrl+B)"
          >
            <Bold size={14} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            disabled={disabled}
            title="Kursywa (Ctrl+I)"
          >
            <Italic size={14} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            disabled={disabled}
            title="Podkreślenie (Ctrl+U)"
          >
            <UnderlineIcon size={14} />
          </ToolbarButton>

          <div className="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-1" />

          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            disabled={disabled}
            title="Lista punktowana"
          >
            <List size={14} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            disabled={disabled}
            title="Lista numerowana"
          >
            <ListOrdered size={14} />
          </ToolbarButton>

          {onImageUpload && (
            <>
              <div className="w-px h-4 bg-gray-200 dark:bg-gray-600 mx-1" />
              <ToolbarButton
                onClick={() => imageInputRef.current?.click()}
                disabled={disabled || uploadingImage}
                title="Wstaw obraz"
              >
                {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
              </ToolbarButton>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageFileRef.current(file);
                  e.target.value = "";
                }}
              />
            </>
          )}
        </div>

        {/* Editor area */}
        <div
          className={[
            "px-3 py-2 relative overflow-y-auto",
            isDragOver ? "bg-[var(--color-primary-dim)]" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={{ height: editorHeight }}
        >
          {editor.isEmpty && !isDragOver && (
            <span className="absolute top-2 left-3 text-sm text-gray-400 dark:text-gray-500 pointer-events-none select-none">
              {placeholder}
            </span>
          )}
          {isDragOver && onImageUpload && (
            <span className="absolute top-2 left-3 text-sm text-[var(--color-primary)] pointer-events-none select-none font-medium">
              Upuść obraz aby wstawić…
            </span>
          )}
          <EditorContent editor={editor} className="h-full" />
        </div>

        {/* Upload error */}
        {uploadError && (
          <p className="px-3 py-1 text-xs text-red-600 dark:text-red-400 border-t border-red-100 dark:border-red-900">
            {uploadError}
          </p>
        )}

        {/* Resize handle */}
        <div
          onMouseDown={onResizeStart}
          className="flex items-center justify-center h-4 border-t border-gray-100 dark:border-gray-700 cursor-row-resize bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
          title="Przeciągnij aby zmienić wysokość"
        >
          <GripHorizontal size={12} />
        </div>
      </div>

      {/* Mentioned users chips */}
      {mentionSuggestions && mentionSuggestions.length > 0 && (
        <div className="flex gap-1.5 flex-wrap items-center min-h-[1.5rem] mt-1.5">
          {mentionedIds.map((id) => {
            const u = mentionSuggestionsRef.current?.find((x) => x.id === id);
            return u ? (
              <button
                key={id}
                type="button"
                onClick={() => removeMention(id)}
                className="text-xs font-mono bg-[var(--color-primary-dim)] text-[var(--color-primary)] px-2 py-0.5 border border-[var(--color-primary)] hover:bg-red-50 hover:text-red-600 transition-colors flex items-center gap-1"
                title="Kliknij, aby usunąć"
              >
                @{u.name} <X size={10} />
              </button>
            ) : null;
          })}
          <button
            ref={addBtnRef}
            type="button"
            onClick={openAddPicker}
            disabled={disabled}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] flex items-center gap-0.5 transition-colors disabled:opacity-40"
            title="Dodaj oznaczenie"
          >
            <Plus size={11} /> Dodaj
          </button>
        </div>
      )}

      {/* Add-mention picker dropdown */}
      {addOpen && addDropPos && createPortal(
        <div
          style={{ position: "fixed", top: addDropPos.top, bottom: addDropPos.bottom, left: addDropPos.left, zIndex: 9999, minWidth: 220 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg max-h-52 overflow-y-auto"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="p-1.5 border-b border-gray-100 dark:border-gray-700">
            <input
              autoFocus
              type="text"
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              placeholder="Szukaj…"
              className="w-full text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:border-[var(--color-primary)]"
            />
          </div>
          {(mentionSuggestionsRef.current ?? [])
            .filter((s) => s.name.toLowerCase().includes(addSearch.toLowerCase()))
            .slice(0, 8)
            .map((u) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={() => addMentionFromPicker(u)}
                className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <span className="w-6 h-6 flex-shrink-0 bg-[var(--color-primary-dim)] text-[var(--color-primary)] text-xs font-bold flex items-center justify-center">
                  {u.name.charAt(0)}
                </span>
                <div>
                  <p className="font-medium text-gray-800 dark:text-gray-200 leading-tight">{u.name}</p>
                  {u.role && (
                    <p className="text-xs text-gray-400">{getRoleLabel(u.role, u.is_ceo)}</p>
                  )}
                </div>
              </button>
            ))}
        </div>,
        document.body
      )}

      {/* Mention suggestion dropdown */}
      {mentionDropdown && createPortal(
        <div
          style={{ position: "fixed", top: mentionDropdown.top, left: mentionDropdown.left, zIndex: 9999, minWidth: 220 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg max-h-52 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {mentionDropdown.users.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={() => insertMention(editor, u)}
              className={[
                "w-full text-left px-3 py-2 text-sm flex items-center gap-2",
                i === mentionDropdown.activeIdx
                  ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                  : "hover:bg-gray-50 dark:hover:bg-gray-700",
              ].join(" ")}
            >
              <span className="w-6 h-6 flex-shrink-0 bg-[var(--color-primary-dim)] text-[var(--color-primary)] text-xs font-bold flex items-center justify-center">
                {u.name.charAt(0)}
              </span>
              <div>
                <p className="font-medium text-gray-800 dark:text-gray-200 leading-tight">{u.name}</p>
                {u.role && (
                  <p className="text-xs text-gray-400">{getRoleLabel(u.role, u.is_ceo)}</p>
                )}
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
